import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Box,
    Stack,
    IconButton,
} from '@mui/material';
import type { PopDisplays_ChannelFrame } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import type { OptionSchema } from 'c-components';
import {
    Alert,
    Button,
    Checkbox,
    DialogV2,
    EntityAutocomplete,
    ListItemText,
    MuiRouterLink,
} from 'c-components';
import { useBoolean } from 'react-hanger';
import { useAPIClientRequest } from 'c-hooks';
import apiClient from 'c-data/apiClient';

import { useCommonTranslation } from 'c-translation';
import { NetworkRequestState } from 'c-data-layer';
import to from 'await-to-js';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEntityData } from 'c-data';
import { generatePath } from 'react-router-dom';
import { PostAuthRoutes } from 'c-routes';

const attachPerms = [PermissionName.DisplaysDisplay_channel_frameCreate];
const detachPerms = [PermissionName.DisplaysDisplay_channel_frameDestroy];

const withoutDisplaysScope = ['withoutDisplays'];
const labelGen = (frame: PopDisplays_ChannelFrame) =>
    `${frame.source} | ${frame.channel} | ${frame.frame} | ${frame.uuid} | ${frame.player} | ${frame.id}`;

const renderOption = (optionProps, option: OptionSchema, frame: PopDisplays_ChannelFrame) => (
    <li {...optionProps}>
        <ListItemText
            primary={frame.channel}
            secondaryTypographyProps={{ component: 'div' } as object}
            secondary={
                <>
                    <Box>
                        <Stack direction="row">
                            {frame.source} | {frame.frame} | {frame.player}
                        </Stack>
                    </Box>
                    <Box>{frame.uuid}</Box>
                </>
            }
        />
    </li>
);
const AddFrames: React.FC<{ displayId: number; onChanged: () => Promise<void> | void }> = ({
    displayId,
    onChanged,
}) => {
    const dialogState = useBoolean(false);
    const {
        start: AttachFrames,
        isLoading: IsAttaching,
        hasFailed: FailedAttaching,
        error: AttachError,
        requestStateRef,
    } = useAPIClientRequest(apiClient.Entities.Displays_Screen.addChannelFrame);

    const t = useCommonTranslation();
    const scopeCheckboxState = useBoolean(true);
    const [frames, setFrames] = useState<number[]>([]);

    const attachFrames = useCallback(async () => {
        if (frames.length === 0 || requestStateRef.current === NetworkRequestState.InProgress)
            return;

        const [err, succ] = await to(AttachFrames(displayId, { channel_frame_ids: frames }));

        if (!err && succ?.data) {
            await onChanged?.();
            dialogState.setFalse();
        }
    }, [frames, requestStateRef, AttachFrames, displayId, onChanged, dialogState]);

    useEffect(() => {
        setFrames([]);
    }, [dialogState.value]);

    return (
        <Box pt={2}>
            <IfHasAllPermissions permissions={attachPerms}>
                <Button onClick={dialogState.setTrue}>
                    {t('Modules.PopDisplays.ChannelFrame.table.addFrameBtn')}
                </Button>
            </IfHasAllPermissions>
            <DialogV2
                onClose={dialogState.setFalse}
                open={dialogState.value}
                title="Modules.PopDisplays.ChannelFrame.table.addFrameTitle"
                actions={
                    <>
                        {FailedAttaching && (
                            <Box mb={2}>
                                <Alert variant="outlined" severity="error">
                                    {String(AttachError)}
                                </Alert>
                            </Box>
                        )}
                        <IfHasAllPermissions permissions={attachPerms}>
                            <Button onClick={attachFrames} disabled={IsAttaching}>
                                {t('Modules.PopDisplays.ChannelFrame.table.addFrameBtn')}
                            </Button>
                        </IfHasAllPermissions>
                    </>
                }
            >
                <Checkbox
                    isBoolean
                    value={scopeCheckboxState.value}
                    onChange={scopeCheckboxState.setValue}
                    label={t('Modules.PopDisplays.ChannelFrame.table.addFrameNoDisplayCheckbox')}
                />
                <EntityAutocomplete
                    key={scopeCheckboxState.value ? '1' : '0'}
                    value={frames}
                    onChange={setFrames}
                    entityName="PopDisplays_ChannelFrame"
                    multiple
                    textFieldProps={{
                        label: t('Modules.PopDisplays.ChannelFrame.table.addFrameDropdown'),
                    }}
                    searchColumns={['channel', 'id', 'frame']}
                    labelColumn="channel"
                    labelGen={labelGen}
                    renderOption={renderOption}
                    scopes={scopeCheckboxState.value ? withoutDisplaysScope : undefined}
                    disableSearchFieldIdFallback
                />
            </DialogV2>
        </Box>
    );
};

const ChannelFrame = ({ data, id }) => {
    const t = useCommonTranslation();
    const [deleteFrameId, setDeleteFrameId] = useState<number>(null);
    const closeDeleteFrame = useCallback(() => {
        setDeleteFrameId(null);
    }, []);
    const deleteFrame = useMemo(
        () => data.find(f => f.id === deleteFrameId),
        [data, deleteFrameId],
    );
    const deleteFrameTitle = useMemo(
        () =>
            t('Modules.PopDisplays.ChannelFrame.table.removeFrameTitle', {
                name: deleteFrame?.channel,
            }),
        [t, deleteFrame],
    );
    const { upsertEntity } = useEntityData('Displays_Screen');
    const {
        start: DetachFrames,
        isLoading: IsDetaching,
        hasFailed: FailedDetaching,
        error: DetachError,
        requestStateRef,
    } = useAPIClientRequest(apiClient.Entities.Displays_Screen.removeChannelFrame);
    const { start: fetchDisplay } = useAPIClientRequest(apiClient.Entities.Displays_Screen.get);
    const refreshDisplay = useCallback(async () => {
        const [gErr, res] = await to(fetchDisplay(id, ['channelFrame']));
        if (!gErr && res?.data?.data) {
            upsertEntity(res.data.data);
        }
    }, [fetchDisplay, id, upsertEntity]);
    const detachFrames = useCallback(async () => {
        if (requestStateRef.current === NetworkRequestState.InProgress) return;

        const [err, succ] = await to(DetachFrames(id, { channel_frame_ids: [deleteFrameId] }));

        if (!err && succ?.data) {
            await refreshDisplay();
            closeDeleteFrame();
        }
    }, [requestStateRef, DetachFrames, id, deleteFrameId, refreshDisplay, closeDeleteFrame]);

    return (
        <>
            <DialogV2
                onClose={closeDeleteFrame}
                open={deleteFrame != null}
                title={deleteFrameTitle}
                description="Modules.PopDisplays.ChannelFrame.table.removeFrameDescription"
                actions={
                    <>
                        {FailedDetaching && (
                            <Box mb={2}>
                                <Alert variant="outlined" severity="error">
                                    {String(DetachError)}
                                </Alert>
                            </Box>
                        )}
                        <IfHasAllPermissions permissions={detachPerms}>
                            <Button color="error" onClick={detachFrames} disabled={IsDetaching}>
                                {t('Modules.PopDisplays.ChannelFrame.table.confirmRemoveFrame')}
                            </Button>
                        </IfHasAllPermissions>
                    </>
                }
            />
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            {t('Modules.Displays.DisplaysDisplayEdit.channelFrames.id')}
                        </TableCell>
                        <TableCell>
                            {t('Modules.Displays.DisplaysDisplayEdit.channelFrames.source')}
                        </TableCell>
                        <TableCell>
                            {t('Modules.Displays.DisplaysDisplayEdit.channelFrames.channel')}
                        </TableCell>
                        <TableCell>
                            {t('Modules.Displays.DisplaysDisplayEdit.channelFrames.frame')}
                        </TableCell>
                        <TableCell>
                            {t('Modules.Displays.DisplaysDisplayEdit.channelFrames.uuid')}
                        </TableCell>
                        <TableCell>
                            {t('Modules.Displays.DisplaysDisplayEdit.channelFrames.player')}
                        </TableCell>
                        <TableCell>
                            {t('Modules.Displays.DisplaysDisplayEdit.channelFrames.firstSeen')}
                        </TableCell>
                        <TableCell>
                            {t('Modules.Displays.DisplaysDisplayEdit.channelFrames.lastSeen')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map(row => (
                        <TableRow key={row.id}>
                            <TableCell>
                                <IfHasAllPermissions permissions={detachPerms}>
                                    <IconButton onClick={() => setDeleteFrameId(row.id)}>
                                        <DeleteIcon color="error" />
                                    </IconButton>
                                </IfHasAllPermissions>
                                <MuiRouterLink
                                    to={generatePath(
                                        PostAuthRoutes.PopDisplays.ChannelFrameOverview,
                                        {
                                            id: row.id,
                                        },
                                    )}
                                >
                                    {row.id}
                                </MuiRouterLink>
                            </TableCell>
                            <TableCell>{row.source}</TableCell>
                            <TableCell>{row.channel}</TableCell>
                            <TableCell>{row.frame}</TableCell>
                            <TableCell>{row.uuid}</TableCell>
                            <TableCell>{row.player}</TableCell>
                            <TableCell>{row.first_seen ?? 'N/A'}</TableCell>
                            <TableCell>{row.last_seen ?? 'N/A'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <AddFrames displayId={id} onChanged={refreshDisplay} />
        </>
    );
};
export default ChannelFrame;
